import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
// @ts-ignore - TypeScript casing issue
import { useSetRecoilState } from 'recoil';
import { Microphone, Stop } from 'phosphor-react';
import { submitSpeaking, getPracticeStatus } from '../../../services/practiceApi';
import { SPEAKING_SUBMIT_FAILED_USER_MESSAGE } from '../../../constants/speakingPracticeMessages';
import {
  getUniversalLearningId,
  getSpeakingAudioMimeType,
  getAudioFileMeta,
  createRecordingId,
  logMediaDiagnostic,
} from './utils/audioUtils';
import { trackErrorReport } from '../../../utils/errorTracker';
import { usePolling } from '../../../hooks/usePolling';
// @ts-ignore - TypeScript casing issue
import { speakingAudioRecordingsState } from '../../../recoil/atoms';
import RecordedAudioPlayer from './components/RecordedAudioPlayer';
import WaveformCanvas from './components/WaveformCanvas';
const Speaking = ({ question, onAnswer, onNext, isLastQuestion = false, sessionData = null, langCode = 'de' }) => {
  const navigate = useNavigate();
  // changed by Cursor: save audio to Recoil instead of localStorage (no quota limits, survives SPA navigation)
  const setAudioRecordings = useSetRecoilState(speakingAudioRecordingsState);
  const [isRecording, setIsRecording] = useState(false);
  const [recordedAudio, setRecordedAudio] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [duration, setDuration] = useState(0);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionResult, setSubmissionResult] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  const [requestId, setRequestId] = useState(null);
  const [submissionError, setSubmissionError] = useState(null);
  // Retry state: tracks how many retranscription retries have been attempted (max 2)
  const [retryCount, setRetryCount] = useState(0);
  const MAX_RETRIES = 2;
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingTimerRef = useRef(null);
  const startTimeRef = useRef(null);
  const requestIdRef = useRef(null);
  const submitInFlightRef = useRef(false);
  // Mime type used for recording (Safari uses audio/mp4, Chrome uses audio/webm)
  const recordingMimeTypeRef = useRef(null);
  // Per-recording correlation id. Reset on rerecord; preserved across retries of the same blob.
  const recordingIdRef = useRef(null);
  const audioFileMetaRef = useRef(null);
  // Track semua blob URLs yang dibuat untuk cleanup
  const blobUrlsRef = useRef([]);
  // Track when question was first shown (for duration calculation)
  const questionStartTimeRef = useRef(null);
  // Web Audio analyser for waveform visualization
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);

  // changed by Cursor: separate text and question
  // Extract text (instruction) and question (the actual question to answer)
  const text = question.text || question['text-soal'] || question.prompt || '';
  const questionText = question.question || question['text-to-read'] || question.prompt || '';
  const prompt = text || questionText || ''; // Keep for backward compatibility

  // Cleanup on unmount
  useEffect(() => {
    const currentAudioUrl = audioUrl;
    const currentTimer = recordingTimerRef.current;
    
    return () => {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
      // Cleanup semua blob URLs
      blobUrlsRef.current.forEach(url => {
        try {
          URL.revokeObjectURL(url);
        } catch (e) {
          // Ignore errors
        }
      });
      if (currentAudioUrl && currentAudioUrl.startsWith('blob:')) {
        try {
          URL.revokeObjectURL(currentAudioUrl);
        } catch (e) {
          // Ignore errors
        }
      }
      if (currentTimer) {
        clearInterval(currentTimer);
      }
      if (audioContextRef.current) {
        audioContextRef.current.close().catch(() => {});
        audioContextRef.current = null;
        analyserRef.current = null;
      }
      blobUrlsRef.current = [];
    };
  }, []); // Empty dependency - only cleanup on unmount

  // Update ref when requestId changes
  useEffect(() => {
    requestIdRef.current = requestId;
  }, [requestId]);

  // Polling hook for checking submission status
  const { data: statusData, startPolling, stopPolling } = usePolling(
    () => {
      if (!requestIdRef.current) {
        console.log('⚠️ Polling function called but requestIdRef.current is null');
        return Promise.resolve(null);
      }
      console.log('🔄 Polling: Calling getPracticeStatus with request-id:', requestIdRef.current);
      return getPracticeStatus(requestIdRef.current, {
        page: 'SpeakingPractice',
        action: 'poll-status',
        practiceSessionId: sessionData?.['session-id'] || null,
      });
    },
    7000,
    (result) => {
      console.log('🔄 Polling: Checking if should stop polling, result:', result);
      // Stop polling only if:
      // 1. Status is 'ok' AND has data (completed successfully)
      // 2. HTTP 500 — BE itself is broken (fatal, not retryable)
      // Continue polling for 'processing' or '200 + error' (retryable per async grading contract)
      if (!result) return false;

      if (result.status === 'ok' && result.data) {
        console.log('✅ Polling: Should stop, status ok with data');
        return true;
      }
      if (result.httpStatus >= 500) {
        console.log('❌ Polling: Should stop, fatal BE error (httpStatus:', result.httpStatus, ')');
        return true;
      }
      console.log('🔄 Polling: Continues, status:', result.status, 'httpStatus:', result.httpStatus);
      return false;
    }
  );

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      stopPolling();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only cleanup on unmount - stopPolling is stable from usePolling hook

  // Handle status polling result
  useEffect(() => {
    if (!statusData || !isSubmitting || !requestId) return;

    if (statusData.status === 'ok' && statusData.data) {
      stopPolling();
      submitInFlightRef.current = false;
      setIsSubmitting(false);
      setSubmitError(null);
      setSubmissionResult(statusData.data);
      
      // Calculate duration from question start to now (in milliseconds)
      let questionDurationMs = 0;
      if (questionStartTimeRef.current) {
        questionDurationMs = Date.now() - questionStartTimeRef.current;
        console.log('⏱️ Speaking - Question duration (from question start to answer):', questionDurationMs, 'ms');
      }
      
      if (onAnswer) {
        onAnswer({
          questionId: question['soal-id'] || question.id,
          prosetId: question['proset-id'] || question.prosetId,
          type: question.type || question['question-type'] || 'speaking',
          answer: recordedAudio,
          userAnswer: statusData.data?.['user-answer'] || questionText || prompt,
          duration: questionDurationMs, // Duration from question start to answer (in ms)
          submissionResult: statusData.data,
          isCorrect: false
        });
      }
    } else if (statusData.status === 'error' && statusData.httpStatus >= 500) {
      stopPolling();
      submitInFlightRef.current = false;
      setIsSubmitting(false);
      setSubmitError(statusData.message || SPEAKING_SUBMIT_FAILED_USER_MESSAGE);
    }
    // 200 + status:"error" = retryable per async grading contract — keep polling silently
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusData, isSubmitting, requestId, recordedAudio, duration, questionText, prompt]);

  // Reset when question changes and set question start time
  useEffect(() => {
    submitInFlightRef.current = false;
    setRecordedAudio(null);
    setAudioUrl(null);
    setIsRecording(false);
    setDuration(0);
    setRecordingDuration(0);
    setIsSubmitting(false);
    setSubmissionResult(null);
    setSubmitError(null);
    setRequestId(null);
    setSubmissionError(null);
    setRetryCount(0);
    stopPolling();
    audioChunksRef.current = [];
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
    }
    // Set question start time when question changes (for duration calculation)
    questionStartTimeRef.current = Date.now();
    console.log('⏱️ Speaking - Question started at:', questionStartTimeRef.current);
  }, [question['soal-id'] || question.id]); // Removed stopPolling from dependency - it's stable

  const startRecording = async () => {
    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        trackErrorReport({
          errorType: 'media',
          message: 'Microphone access is not available in this browser context',
          code: 'get-user-media-unavailable',
          page: 'SpeakingPractice',
          action: 'start-recording',
          practiceSessionId: sessionData?.['session-id'] || null,
        });
        alert('Microphone access is not available. Use HTTPS or localhost.');
        return;
      }
      if (typeof MediaRecorder === 'undefined' || !window.MediaRecorder) {
        trackErrorReport({
          errorType: 'media',
          message: 'MediaRecorder is not supported in this browser',
          code: 'media-recorder-unsupported',
          page: 'SpeakingPractice',
          action: 'start-recording',
          practiceSessionId: sessionData?.['session-id'] || null,
        });
        alert('Recording is not supported in this browser. Try iOS 14+ or Chrome/Firefox.');
        return;
      }
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      // Set up AnalyserNode for real-time waveform visualization
      try {
        const AudioContextClass = window.AudioContext || window.webkitAudioContext;
        if (AudioContextClass) {
          const audioCtx = new AudioContextClass();
          const source = audioCtx.createMediaStreamSource(stream);
          const analyser = audioCtx.createAnalyser();
          analyser.fftSize = 128;
          source.connect(analyser);
          audioContextRef.current = audioCtx;
          analyserRef.current = analyser;
        }
      } catch (e) {
        console.warn('Could not create AudioContext for waveform visualization:', e);
      }

      const mimeType = getSpeakingAudioMimeType();
      recordingMimeTypeRef.current = mimeType || 'audio/webm';
      const options = mimeType ? { mimeType } : {};
      const mediaRecorder = new MediaRecorder(stream, options);

      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      // Mint recordingId for this recording session (preserved across retries; reset on rerecord)
      const newRecordingId = createRecordingId();
      recordingIdRef.current = newRecordingId;
      logMediaDiagnostic('recording.start', {
        recordingId: newRecordingId,
        startedAt: new Date().toISOString(),
        configuredMimeType: mimeType || null,
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
        practiceSessionId: sessionData?.['session-id'] || null,
      });

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        const recorderMimeType = mediaRecorderRef.current?.mimeType || recordingMimeTypeRef.current || 'audio/webm';
        const chunkCount = audioChunksRef.current.length;
        const audioBlob = new Blob(audioChunksRef.current, { type: recorderMimeType });
        const fileMeta = getAudioFileMeta(audioBlob.type || recorderMimeType);
        audioFileMetaRef.current = fileMeta;
        setRecordedAudio(audioBlob);
        
        // Create URL for playback
        const url = URL.createObjectURL(audioBlob);
        // Track blob URL untuk cleanup
        blobUrlsRef.current.push(url);
        setAudioUrl(url);
        
        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());

        // Clean up audio context used for waveform
        if (audioContextRef.current) {
          audioContextRef.current.close().catch(() => {});
          audioContextRef.current = null;
          analyserRef.current = null;
        }
        
        // Calculate duration from start time (already in milliseconds)
        const durationMs = Date.now() - startTimeRef.current;
        const durationInSeconds = Math.floor(durationMs / 1000);
        setDuration(durationMs);
        setRecordingDuration(durationInSeconds);
        
        // changed by Cursor: store answer immediately so user can proceed to next question
        // (will be updated with transcription when per-question submission completes)
        if (onAnswer) {
          let questionDurationMs = 0;
          if (questionStartTimeRef.current) {
            questionDurationMs = Date.now() - questionStartTimeRef.current;
          }
          onAnswer({
            questionId: question['soal-id'] || question.id,
            prosetId: question['proset-id'] || question.prosetId,
            type: question.type || question['question-type'] || 'speaking',
            answer: audioBlob,
            userAnswer: questionText || prompt,
            duration: questionDurationMs,
            submissionResult: null,
            isCorrect: false
          });
        }

        // changed by Cursor: save audio to Recoil state (replaces localStorage - no quota limits, survives SPA navigation)
        const questionId = question['soal-id'] || question.soalId || question.id || question.originalQuestion?.['soal-id'] || question.originalQuestion?.soalId || question.originalQuestion?.id || null;
        const taskIndex = ((question.number || 1) - 1).toString();

        if (!questionId) {
          console.warn('⚠️ Speaking - No soal-id found for question:', { questionNumber: question.number });
        }

        // Convert blob to base64 and save to Recoil (survives SPA navigation, no quota limits)
        const reader = new FileReader();
        reader.onloadend = () => {
          try {
            const base64String = reader.result;
            setAudioRecordings(prev => ({
              ...prev,
              [taskIndex]: {
                audioUrl: base64String,
                soalId: questionId,
              },
            }));
            console.log('✅ Speaking - Saved audio to Recoil:', { taskIndex, questionId });
          } catch (error) {
            console.error('❌ Speaking - Error saving audio to Recoil:', error);
          }
        };
        reader.readAsDataURL(audioBlob);
        
        // BE accepts webm/m4a directly via Whisper — no client-side WAV conversion.
        logMediaDiagnostic('recording.stop', {
          recordingId: recordingIdRef.current,
          intendedDurationMs: durationMs,
          blobSize: audioBlob.size,
          blobType: audioBlob.type,
          chunkCount,
          recorderMimeType,
          practiceSessionId: sessionData?.['session-id'] || null,
        });
        handleSubmitSpeaking(audioBlob, durationMs);
      };

      mediaRecorder.start();
      setIsRecording(true);
      startTimeRef.current = Date.now();
      
      // Start timer
      recordingTimerRef.current = setInterval(() => {
        setRecordingDuration(Math.floor((Date.now() - startTimeRef.current) / 1000));
      }, 1000);
    } catch (error) {
      console.error('Error starting recording:', error);
      trackErrorReport({
        errorType: 'media',
        message: error?.message || 'Failed to start audio recording',
        code: error?.name || 'start-recording-failed',
        stack: error?.stack,
        page: 'SpeakingPractice',
        action: 'start-recording',
        practiceSessionId: sessionData?.['session-id'] || null,
      });
      alert('Failed to access microphone. Please check your permissions.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
        recordingTimerRef.current = null;
      }
    }
    // analyserRef/audioContextRef are cleaned up in mediaRecorder.onstop
  };

  const handleRerecordSpeaking = () => {
    submitInFlightRef.current = false;
    setSubmitError(null);
    setSubmissionResult(null);
    setRequestId(null);
    requestIdRef.current = null;
    setRetryCount(0);
    stopPolling();
    setIsSubmitting(false);
    if (audioUrl && audioUrl.startsWith('blob:')) {
      try {
        URL.revokeObjectURL(audioUrl);
      } catch (e) {
        /* ignore */
      }
      blobUrlsRef.current = blobUrlsRef.current.filter((u) => u !== audioUrl);
    }
    setRecordedAudio(null);
    setAudioUrl(null);
    setDuration(0);
    setRecordingDuration(0);
    recordingIdRef.current = null;
    audioFileMetaRef.current = null;
    const taskIndex = ((question.number || 1) - 1).toString();
    setAudioRecordings((prev) => {
      const next = { ...prev };
      delete next[taskIndex];
      return next;
    });
  };

  const handleRetry = () => {
    if (!recordedAudio) return;
    submitInFlightRef.current = false;
    setSubmitError(null);
    setRequestId(null);
    requestIdRef.current = null;
    stopPolling();
    setRetryCount((prev) => prev + 1);
    handleSubmitSpeaking(recordedAudio, duration);
  };

  const handleSubmitSpeaking = async (audioBlob, recordingDurationMs) => {
    if (!audioBlob) return;
    if (submitInFlightRef.current) {
      return;
    }
    submitInFlightRef.current = true;

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const universalLearningId = getUniversalLearningId();
      
      if (!universalLearningId) {
        console.log('❌ No learner-id found, redirecting to login...');
        setIsSubmitting(false);
        localStorage.removeItem('token');
        localStorage.removeItem('userData');
        localStorage.removeItem('universal-learning-id');
        navigate('/learner/login');
        return;
      }

      // Calculate duration from question start to now (in milliseconds)
      // Duration = time from question shown until submission
      let questionDurationMs = 0;
      if (questionStartTimeRef.current) {
        questionDurationMs = Date.now() - questionStartTimeRef.current;
        console.log('⏱️ Speaking - Question duration (from question start to submission):', questionDurationMs, 'ms');
      } else {
        // Fallback to recording duration if questionStartTimeRef is not set
        questionDurationMs = recordingDurationMs;
        console.warn('⚠️ Speaking - questionStartTimeRef not set, using recording duration as fallback');
      }

      const lessonId = sessionData?.['lesson-id'] || question['lesson-id'] || null;
      const courseId = sessionData?.['course-id'] || question['course-id'] || null;
      const subcourseId = sessionData?.['subcourse-id'] || question['subcourse-id'] || null;
      const sessionId = sessionData?.['session-id'] || question['session-id'] || null;
      const prosetId = question['proset-id'] || question.prosetId || question['proset_id'] || null;
      const soalId = question['soal-id'] || question.id || null;
      // changed by Cursor: get lang-code from sessionData or prop
      const effectiveLangCode = sessionData?.['lang-code'] || langCode || null;

      logMediaDiagnostic('upload.dispatch', {
        recordingId: recordingIdRef.current,
        audioFilename: audioFileMetaRef.current?.filename || null,
        audioContentType: audioFileMetaRef.current?.contentType || null,
        blobSize: audioBlob?.size ?? null,
        practiceSessionId: sessionId || null,
      });

      const result = await submitSpeaking({
        sessionId,
        lessonId,
        courseId,
        subcourseId,
        prosetId,
        soalId,
        duration: questionDurationMs, // Duration from question start to submission (in ms)
        universalLearningId,
        audioBlob,
        langCode: effectiveLangCode,
        audioFilename: audioFileMetaRef.current?.filename || undefined,
        audioContentType: audioFileMetaRef.current?.contentType || undefined,
        recordingId: recordingIdRef.current,
      });
      if (result.success) {
        // Check if response has request-id for async processing
        // request-id can be in result.requestId (from API wrapper) or result.data['request-id']
        const receivedRequestId = 
          result.requestId ||  // From API wrapper
          result.data?.['request-id'] || 
          result.data?.requestId ||
          null;
        
        console.log('🔍 Extracted request-id:', receivedRequestId);
        console.log('🔍 result.requestId:', result.requestId);
        console.log('🔍 result.data:', result.data);
        
        if (receivedRequestId) {
          console.log('✅ Starting polling for request-id:', receivedRequestId);
          // Async processing - set requestId and ref, then start polling
          // Polling hook will handle the first status check automatically
          setRequestId(receivedRequestId);
          requestIdRef.current = receivedRequestId;
          
          // Start polling immediately - hook will call getPracticeStatus
          startPolling();
        } else {
          submitInFlightRef.current = false;
          setIsSubmitting(false);
          if (result.data != null) {
            setSubmissionResult(result.data);

            let questionDurationMsSync = 0;
            if (questionStartTimeRef.current) {
              questionDurationMsSync = Date.now() - questionStartTimeRef.current;
              console.log('⏱️ Speaking - Question duration (from question start to answer, sync):', questionDurationMsSync, 'ms');
            }

            if (onAnswer) {
              onAnswer({
                questionId: question['soal-id'] || question.id,
                prosetId: question['proset-id'] || question.prosetId,
                type: question.type || question['question-type'] || 'speaking',
                answer: audioBlob,
                userAnswer: result.data?.['user-answer'] || questionText || prompt,
                duration: questionDurationMsSync,
                submissionResult: result.data,
                isCorrect: false
              });
            }
          } else {
            setSubmitError(result.message || SPEAKING_SUBMIT_FAILED_USER_MESSAGE);
          }
        }
      } else {
        submitInFlightRef.current = false;
        throw new Error(result.message || SPEAKING_SUBMIT_FAILED_USER_MESSAGE);
      }
    } catch (error) {
      submitInFlightRef.current = false;
      console.error('Error submitting speaking:', error);
      setIsSubmitting(false);
      stopPolling();
      
      // Check if error is about learner-id - interceptor will handle redirect
      const errorMessage = error?.message || '';
      const errorMessageLower = errorMessage.toLowerCase();
      const isLearnerIdError = errorMessageLower.includes('learner-id') || 
                               errorMessageLower.includes('learner id') ||
                               errorMessageLower.includes('learner-id is required') ||
                               errorMessageLower.includes('learner-id required') ||
                               errorMessageLower.includes('learner-id missing') ||
                               errorMessageLower.includes('must provide learner-id');
      
      if (isLearnerIdError) {
        console.log('❌ Submit failed due to learner-id error');
        return;
      }

      const apiMsg = error?.response?.data?.message;
      setSubmitError(
        typeof apiMsg === 'string' && apiMsg.trim()
          ? apiMsg
          : error?.message || SPEAKING_SUBMIT_FAILED_USER_MESSAGE
      );
    }
  };

  const handleNext = () => {
    if (isSubmitting || submitError || submissionError) return;

    // Calculate final duration from question start to next (in milliseconds)
    let questionDurationMs = 0;
    if (questionStartTimeRef.current) {
      questionDurationMs = Date.now() - questionStartTimeRef.current;
      console.log('⏱️ Speaking - Question duration (from question start to next):', questionDurationMs, 'ms');
    }
    
    // Update duration in answer if not already set (fallback)
    if (duration === 0 && questionDurationMs > 0) {
      setDuration(questionDurationMs);
    }
    
    if (onNext) onNext();
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const isNextDisabled =
    !recordedAudio ||
    isRecording ||
    isSubmitting ||
    !!submitError ||
    (!!submissionError && !isSubmitting);

  return (
    <motion.div
      className="flex flex-col gap-5 max-md:gap-4 max-md:pb-10"
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
    >
      <div className="flex justify-between items-center pb-4 border-b-2 border-[rgba(18,118,109,0.1)] dark:border-[var(--border-color)] max-md:flex-col max-md:items-start max-md:gap-3">
        <motion.span
          className="px-3.5 py-2 bg-gradient-to-br from-[#12766d] to-[#236463] text-white rounded-lg text-[11px] font-semibold uppercase tracking-wide whitespace-nowrap shrink-0 max-md:text-[10px] max-md:px-2 max-md:py-1"
          initial={{ scale: 0.85 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', stiffness: 300 }}
        >
          Speaking
        </motion.span>
      </div>

      <div className="flex flex-col gap-5">
        {text && (
          <div className={`p-5 bg-[rgba(18,118,109,0.05)] dark:bg-[rgba(20,184,166,0.08)] rounded-xl border-l-4 border-[#12766d] dark:border-[#14b8a6] max-md:p-4 ${questionText ? 'mb-5' : 'mb-0'}`}>
            <div
              className="passage-text text-base leading-relaxed text-[var(--text-primary)] whitespace-pre-line"
              dangerouslySetInnerHTML={{ __html: text }}
            />
          </div>
        )}

        {!text && !questionText && prompt && (
          <p
            className="question-text mb-4 text-lg font-semibold text-[var(--text-primary)] leading-relaxed whitespace-pre-line"
            dangerouslySetInnerHTML={{ __html: prompt }}
          />
        )}

        {questionText && (
          <div className="mb-4 p-4 rounded-lg bg-[var(--bg-tertiary)] border-l-4 border-[#146e69] dark:border-[#14b8a6]">
            <p className="question-text m-0 mb-3 text-lg font-semibold text-[var(--text-primary)]">
              Question:
            </p>
            <p
              className="question-text m-0 text-lg font-medium text-[var(--text-primary)] leading-relaxed"
              dangerouslySetInnerHTML={{ __html: questionText.replace(/\n/g, '') }}
            />
          </div>
        )}

        <div className="mt-[30px] max-md:mt-1.5 flex flex-col items-center gap-5 max-md:gap-4">
          {!recordedAudio || isSubmitting ? (
            <div className="flex flex-col items-center gap-5 max-md:gap-4">
              {!isRecording ? (
                <button
                  type="button"
                  data-track-action="speaking-start-recording"
                  className="flex flex-col items-center justify-center gap-2 w-[180px] h-[180px] max-md:w-[140px] max-md:h-[140px] rounded-full border-none p-0 text-xs font-semibold text-white cursor-pointer transition-all duration-300 font-[inherit] bg-gradient-to-br from-gray-500 to-gray-600 shadow-[0_4px_12px_rgba(107,114,128,0.3)] hover:shadow-[0_6px_16px_rgba(107,114,128,0.4)] disabled:opacity-60 disabled:cursor-not-allowed"
                  onClick={startRecording}
                  disabled={isSubmitting}
                >
                  <Microphone size={32} weight="fill" />
                  <span>{isSubmitting ? 'Submitting...' : 'Click to Record'}</span>
                </button>
              ) : (
                <div className="flex flex-col items-center gap-4">
                  <button
                    type="button"
                    data-track-action="speaking-stop-recording"
                    className="flex flex-col items-center justify-center gap-2 w-[180px] h-[180px] max-md:w-[140px] max-md:h-[140px] rounded-full border-none p-0 text-xs font-semibold text-white cursor-pointer transition-all duration-300 font-[inherit] bg-gradient-to-br from-red-500 to-red-600 shadow-[0_4px_12px_rgba(239,68,68,0.3)] hover:shadow-[0_6px_16px_rgba(239,68,68,0.4)]"
                    onClick={stopRecording}
                  >
                    <Stop size={32} weight="fill" />
                    <span>Stop Recording</span>
                  </button>
                  <div className="flex items-center gap-3 max-md:gap-2.5 text-lg max-md:text-base font-semibold text-red-500">
                    <span className="w-3 h-3 max-md:w-2.5 max-md:h-2.5 rounded-full bg-red-500 animate-[pronunciation-pulse_1.5s_ease-in-out_infinite]" />
                    <span className="font-mono">{formatTime(recordingDuration)}</span>
                  </div>
                  <WaveformCanvas isRecording={isRecording} analyserNode={analyserRef} />
                </div>
              )}
            </div>
          ) : (
            <div className="w-full">
              <RecordedAudioPlayer audioUrl={audioUrl} />
              {submitError && (
                <div
                  role="alert"
                  className="mt-4 rounded-xl border border-red-300/80 bg-red-50 px-4 py-3 text-[15px] leading-snug text-red-800 dark:border-red-500/40 dark:bg-red-950/35 dark:text-red-100 max-md:mt-3 max-md:px-3.5 max-md:py-2.5 max-md:text-sm"
                >
                  <p className="m-0 mb-3 font-medium">{submitError}</p>
                  {retryCount < MAX_RETRIES ? (
                    <button
                      type="button"
                      data-track-action="speaking-retry-transcribe"
                      className="w-full rounded-lg border-none px-4 py-2 text-sm font-semibold text-white cursor-pointer transition-colors bg-red-500 hover:bg-red-600 dark:bg-red-600 dark:hover:bg-red-700 max-md:min-h-[44px] disabled:opacity-60 disabled:cursor-not-allowed"
                      onClick={handleRetry}
                      disabled={isSubmitting}
                    >
                      Retry ({MAX_RETRIES - retryCount} attempts left)
                    </button>
                  ) : (
                    <div className="flex flex-col gap-2">
                      <p className="m-0 text-sm text-red-600 dark:text-red-400">
                        Transcription still failed after {MAX_RETRIES} retries. Please re-record.
                      </p>
                      <button
                        type="button"
                        data-track-action="speaking-rerecord-inline"
                        className="w-full rounded-lg border-none px-4 py-2 text-sm font-semibold text-white cursor-pointer transition-colors bg-[#12766d] hover:bg-[#0f5d56] dark:bg-[#14b8a6] dark:hover:bg-[#0d9488] max-md:min-h-[44px] disabled:opacity-60 disabled:cursor-not-allowed"
                        onClick={handleRerecordSpeaking}
                        disabled={isSubmitting}
                      >
                        Re-record
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {submissionError && !isSubmitting && (
        <div className="w-full flex flex-col items-center gap-3 p-4 bg-red-50 dark:bg-red-900/20 rounded-xl border border-red-200 dark:border-red-800">
          <p className="text-sm text-red-600 dark:text-red-400 font-medium text-center m-0">{submissionError}</p>
          <button
            type="button"
            data-track-action="speaking-rerecord-footer"
            className="w-full py-2 px-4 text-sm font-semibold rounded-lg border-none cursor-pointer transition-colors bg-[#12766d] hover:bg-[#0f5d56] text-white dark:bg-[#14b8a6] dark:hover:bg-[#0d9488] max-md:min-h-[44px]"
            onClick={handleRerecordSpeaking}
          >
            Re-record
          </button>
        </div>
      )}

      <div className="flex justify-center pt-4 border-t-2 border-[rgba(18,118,109,0.1)] dark:border-[var(--border-color)]">
        <motion.button
          type="button"
          data-track-action={isLastQuestion ? 'speaking-submit-next' : 'speaking-next-question'}
          className={`py-3.5 px-7 rounded-xl border-none text-base font-semibold transition-all duration-300 max-md:w-full max-md:py-3.5 max-md:px-6 max-md:min-h-[44px] max-md:text-[15px] ${
            isNextDisabled
              ? 'cursor-not-allowed opacity-60 bg-slate-400 text-white shadow-none dark:bg-[#334155] dark:text-[#64748b] dark:opacity-70 pointer-events-none'
              : 'cursor-pointer bg-gradient-to-br from-[#12766d] to-[#0f5d56] text-white shadow-[0_4px_12px_rgba(18,118,109,0.3)] hover:shadow-[0_6px_16px_rgba(18,118,109,0.4)] hover:-translate-y-0.5 dark:bg-gradient-to-br dark:from-[#fbbf24] dark:to-[#f59e0b] dark:text-[#1d4746] dark:shadow-[0_4px_14px_rgba(251,191,36,0.35)] dark:hover:from-[#f59e0b] dark:hover:to-[#d97706] dark:hover:shadow-[0_6px_18px_rgba(251,191,36,0.45)]'
          }`}
          onClick={handleNext}
          disabled={isNextDisabled}
          whileTap={isNextDisabled ? undefined : { scale: 0.96 }}
        >
          {isLastQuestion ? 'Submit' : 'Next Question'}
        </motion.button>
      </div>
    </motion.div>
  );
};

export default Speaking;

